import request from '@apps-common/js-client/api/request';

import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {queryKeys as collectionsQueryKeys} from './collections.js';
import {GRANERO_BASE_URL} from '@app/config';

import {IN_PROGRESS_STATES, UPLOAD_STATES} from '@app/constants/uploads.js';

const INGEST_URL = `${GRANERO_BASE_URL}/ingest/`;

export const queryKeys = {
  all: ['uploads'],
  lists: params => [...queryKeys.all, 'list', params],
  collection: upload => [...queryKeys.all, 'collection', upload?.id],
  properties: upload => [...queryKeys.collection(upload), 'properties'],
  reviewList: upload => [...queryKeys.all, 'reviews', upload?.id],
  reviewFeature: (upload, feature) => [
    ...queryKeys.all,
    'review',
    upload?.id,
    feature?.id,
  ],
};

/* return a link to the ingested collection for a given upload */
const getIngestedCollectionUrl = upload => {
  const collectionUrl = upload.links.find(
    link => link.rel === 'ingested-collection'
  )?.href;
  if (!collectionUrl) {
    throw new Error(
      'There was an unexpected error fetching the uploaded collection. Please try again.'
    );
  }
  return collectionUrl;
};

// Upload files to GCS using an array of signed URLs.
// The signed URLs are generated by Granero and should always correspond to the
// entries in the files array.
export const uploadToGCS = (signedUrl, file, config) => {
  return request.put(signedUrl, file, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    authToken: null,
    ...config,
  });
};

// Ingest one or more files.
// Multiple files should only be used for sidecards, not for multiple collections.
// Call ingestFile multiple times for multiple collections.
// config is an optional object that will be added to the axios request config for the
// GCS upload. It is used for tracking request progress.
export const ingestFiles = files => {
  const filenames = files.map(file => file.name);
  const data = {
    upload: {
      files: filenames,
    },
  };

  return request.post(INGEST_URL, data);
};

export const getUploadFeaturesToReview = async ({upload}) => {
  return request
    .get(`${getIngestedCollectionUrl(upload)}/review/`)
    .then(res => res.data);
};

export const getUploadReviewSingleFeature = async ({upload, feature}) => {
  return request
    .get(`${getIngestedCollectionUrl(upload)}/review/${feature.id}/`)
    .then(res => res.data);
};

const uploadNotDone = upload => {
  return (
    upload.state === UPLOAD_STATES.CREATED ||
    upload.state === UPLOAD_STATES.PROCESSING
  );
};

const uploadMigrating = upload => upload.state === UPLOAD_STATES.MIGRATING;
const inProgress = upload => IN_PROGRESS_STATES.includes(upload.state);

// return the count of uploads that need attention
export const countUploadsInProgress = uploads => {
  if (!uploads) {
    return 0;
  }

  return uploads.filter(inProgress).length;
};

const groupUploadsByState = uploads => {
  return uploads.reduce(
    (acc, item) => {
      switch (item.state) {
        case UPLOAD_STATES.CREATED:
        case UPLOAD_STATES.PENDING:
        case UPLOAD_STATES.PROCESSING:
        case UPLOAD_STATES.INGESTED:
        case UPLOAD_STATES.REVIEW:
        case UPLOAD_STATES.MIGRATING:
          acc.toReview.push(item);
          break;
        case UPLOAD_STATES.ERROR:
          acc.errors.push(item);
          break;
        case UPLOAD_STATES.DONE:
          acc.uploaded.push(item);
          break;
        default:
          break;
      }
      return acc;
    },
    {toReview: [], errors: [], uploaded: []}
  );
};

export const listMigrated = uploads => {
  return uploads
    ?.filter(upload => upload.state === UPLOAD_STATES.DONE)
    .map(upload => upload.id);
};

export const listUploads = async states => {
  return request
    .get(INGEST_URL, {
      params: {
        status: states,
      },
    })
    .then(res => res.data);
};
export const approveFeature = async ({upload, feature, outcome}) => {
  const data = {state: outcome};

  return request
    .post(`${getIngestedCollectionUrl(upload)}/review/${feature.id}/`, data)
    .then(res => res.data);
};

export const moveUpload = async ({upload, options = {}}) => {
  return request.post(`${getIngestedCollectionUrl(upload)}/move/`, {
    accept_all: options.acceptAll || null,
    include_props: options.properties || null,
    title_property: options.titleProperty || null,
    description_property: options.descriptionProperty || null,
    id_property: options.idProperty || null,
    collection_title:
      options.collection?.id === 'new'
        ? (options.collection?.title ?? '').trim() || null
        : null, // Don't set the title if we're appending to an existing collection
    collection_target: options.collection?.target || null,
  });
};

export const getUploadCollection = async ({upload}) => {
  return request
    .get(getIngestedCollectionUrl(upload) + '/items/')
    .then(res => res.data);
};

export const useListUploads = ({states, ...queryOptions} = {}) => {
  return useQuery({
    queryKey: queryKeys.lists(states),
    queryFn: () => listUploads(states),
    refetchInterval: ({state}) => {
      if (Array.isArray(state?.data)) {
        // if any of our uploads are being processed, poll until they are done
        // so that the user can see when they're finished
        if (state?.data?.some(uploadNotDone)) {
          return 1000;
        }

        // we're reviewing something and it might be migrated to a real collection
        // temporary: for demo purposes. We need some kind of signal from the API
        // that an ingested feature is currently being migrated.
        if (state?.data?.some(uploadMigrating)) {
          return 2500;
        }
      }

      // if all uploads are complete, stop refetching automatically.
      return Infinity;
    },
    select: data => {
      return {
        list: data,
        uploadsInProgress: countUploadsInProgress(data),
        migrated: listMigrated(data),
        byState: groupUploadsByState(data),
      };
    },
    ...queryOptions,
  });
};

export const useGetUploadReviewList = (upload, queryOptions = {}) => {
  return useQuery({
    queryKey: queryKeys.reviewList(upload),
    queryFn: () => getUploadFeaturesToReview({upload}),
    ...queryOptions,
  });
};

export const useGetUploadReviewSingleFeature = (
  {upload, feature},
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.reviewFeature(upload, feature),
    queryFn: () => getUploadReviewSingleFeature({upload, feature}),
    ...queryOptions,
    gcTime: 10000, // shorter gc time due to the size of the data
  });
};

export const useApproveFeature = ({upload, feature}, mutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: outcome => approveFeature({upload, feature, outcome}),
    ...mutationOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.reviewFeature(upload, feature),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.reviewList(upload),
      });

      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess();
      }
    },
  });
};

export const useFinishUpload = ({upload}, mutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: options => moveUpload({upload, options}),
    ...mutationOptions,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.lists(),
      });

      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess();
      }
    },
  });
};

export const useDeleteUpload = ({upload}, mutationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => request.delete(`${INGEST_URL}${upload.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
    },
    ...mutationOptions,
  });
};

export const useGetUploadCollection = ({upload}, queryOptions = {}) => {
  return useQuery({
    queryKey: queryKeys.collection(upload),
    queryFn: () => getUploadCollection({upload}),
    ...queryOptions,
  });
};

export const useGetUploadCollectionProperties = (
  {upload} = {},
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.properties(upload),
    queryFn: () =>
      request
        .get(`${getIngestedCollectionUrl(upload)}/properties/`)
        .then(res => res.data),
    ...queryOptions,
  });
};
